import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { getBrowseCategoryDisplayLabel } from '../../util/eventCategoryBrowse';
import { NamedLink, OutsideClickHandler } from '../../components';
import { subscribeOpenBrowseMegaMenu } from '../../util/browseMegaMenuEvents';
import { getMegaMenuCategoryImage } from '../../util/megaMenuCategoryImages';
import { useNavigateToAllEventsSearch } from '../../util/navigateToAllEventsSearch';
import { useBrowseMenuTree } from '../../util/useBrowseMenuTree';

import css from './TopbarBrowseCategoriesMegaMenu.module.css';

const KEY_CODE_ESCAPE = 27;

/**
 * Full-width browse mega panel (desktop). Fixed positioning so the panel is not
 * limited to the trigger width (Sharetribe `Menu` sets minWidth to the label).
 */
export const TopbarBrowseCategoriesMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuRefreshKey, setMenuRefreshKey] = useState(0);
  const { menuTree } = useBrowseMenuTree(menuRefreshKey);
  const navigateToAllEventsSearch = useNavigateToAllEventsSearch();

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const handleViewAllEventsClick = useCallback(
    e => {
      e.preventDefault();
      close();
      navigateToAllEventsSearch();
    },
    [close, navigateToAllEventsSearch]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = e => {
      if (e.keyCode === KEY_CODE_ESCAPE) {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  useEffect(() => subscribeOpenBrowseMegaMenu(() => setIsOpen(true)), []);

  useEffect(() => {
    if (isOpen) {
      setMenuRefreshKey(k => k + 1);
    }
  }, [isOpen]);

  if (!menuTree.length) {
    return null;
  }

  return (
    <OutsideClickHandler rootClassName={css.megaMenuRoot} onOutsideClick={close}>
      <button
        type="button"
        className={classNames(css.trigger, { [css.triggerOpen]: isOpen })}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={toggle}
      >
        <FormattedMessage id="TopbarDesktop.browseEventsMegaMenu" />
        <span className={classNames(css.chevron, { [css.chevronOpen]: isOpen })} aria-hidden />
      </button>

      {isOpen ? (
        <div className={css.megaLayer}>
            <div className={css.megaBackdrop} aria-hidden onClick={close} />
            <div className={css.megaPanel} role="navigation" aria-label="Browse events by category">
              <div className={css.megaInner}>
                <div className={css.megaHeader}>
                  <p className={css.megaHeaderTitle}>
                    <FormattedMessage id="TopbarDesktop.browseEventsMegaMenuTitle" />
                  </p>
                  <div className={css.megaHeaderActions}>
                    <NamedLink className={css.viewAllButton} name="SeoVenuesIndexPage" onClick={close}>
                      <FormattedMessage id="TopbarDesktop.browseEventsByVenue" />
                    </NamedLink>
                    <NamedLink className={css.viewAllButton} name="SeoCitiesIndexPage" onClick={close}>
                      <FormattedMessage id="TopbarDesktop.browseEventsByCity" />
                    </NamedLink>
                    <NamedLink
                      className={css.viewAllButton}
                      name="SeoPerformersIndexPage"
                      onClick={close}
                    >
                      <FormattedMessage id="TopbarDesktop.browseEventsByPerformer" />
                    </NamedLink>
                    <NamedLink
                      className={css.viewAllButton}
                      name="SearchPage"
                      onClick={handleViewAllEventsClick}
                    >
                      <FormattedMessage id="TopbarDesktop.browseEventsViewAll" />
                    </NamedLink>
                  </div>
                </div>

                <div className={css.megaColumns}>
                  {menuTree.map(node => {
                    if (!node.slug) {
                      return null;
                    }
                    const parentLabel = getBrowseCategoryDisplayLabel(node);
                    const headerImage = getMegaMenuCategoryImage(node.slug);
                    const children = Array.isArray(node.children) ? node.children : [];
                    return (
                      <section key={node.slug} className={css.megaColumn}>
                        <NamedLink
                          className={css.megaParentHeader}
                          name="BrowseCategoryPage"
                          params={{ slug: node.slug }}
                          onClick={close}
                        >
                          <div className={css.megaParentImageWrap}>
                            {headerImage ? (
                              <img
                                className={css.megaParentImage}
                                src={headerImage}
                                alt=""
                                aria-hidden
                              />
                            ) : null}
                            <span className={css.megaParentTitle}>{parentLabel}</span>
                          </div>
                        </NamedLink>
                        {children.length > 0 ? (
                          <ul className={css.subList}>
                            {children.map(child => {
                              if (!child.slug) {
                                return null;
                              }
                              return (
                                <li key={child.slug}>
                                  <NamedLink
                                    className={css.subLink}
                                    name="BrowseCategoryPage"
                                    params={{ slug: child.slug }}
                                    onClick={close}
                                  >
                                    {getBrowseCategoryDisplayLabel(child)}
                                  </NamedLink>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </section>
                    );
                  })}
                </div>

                <div className={css.megaFooter}>
                  <p className={css.megaFootnote}>
                    <FormattedMessage id="TopbarDesktop.browseEventsMegaMenuFootnote" />
                  </p>
                </div>
              </div>
            </div>
          </div>
      ) : null}
    </OutsideClickHandler>
  );
};

/**
 * Collapsible groups for the hamburger menu (same data as mega menu).
 */
export const TopbarMobileBrowseCategoriesSection = () => {
  const { menuTree } = useBrowseMenuTree();

  if (!menuTree.length) {
    return null;
  }

  return (
    <div className={css.mobileSection}>
      <h2 className={css.mobileSectionTitle}>
        <FormattedMessage id="TopbarMobileMenu.browseEventsCategoriesSection" />
      </h2>
      {menuTree.map(node => {
        if (!node.slug) {
          return null;
        }
        const parentLabel = getBrowseCategoryDisplayLabel(node);
        const children = Array.isArray(node.children) ? node.children : [];
        return (
          <details key={node.slug} className={css.mobileDetails}>
            <summary className={css.mobileSummary}>{parentLabel}</summary>
            <div className={css.mobilePanel}>
              <NamedLink
                className={css.mobileParentLink}
                name="BrowseCategoryPage"
                params={{ slug: node.slug }}
              >
                <FormattedMessage
                  id="TopbarMobileMenu.browseCategoryViewAll"
                  values={{ category: parentLabel }}
                />
              </NamedLink>
              {children.map(child => {
                if (!child.slug) {
                  return null;
                }
                return (
                  <NamedLink
                    key={child.slug}
                    className={css.mobileChildLink}
                    name="BrowseCategoryPage"
                    params={{ slug: child.slug }}
                  >
                    {getBrowseCategoryDisplayLabel(child)}
                  </NamedLink>
                );
              })}
            </div>
          </details>
        );
      })}
      <NamedLink className={css.mobileDirectLink} name="SeoVenuesIndexPage">
        <FormattedMessage id="TopbarMobileMenu.browseEventsByVenue" />
      </NamedLink>
      <NamedLink className={css.mobileDirectLink} name="SeoCitiesIndexPage">
        <FormattedMessage id="TopbarMobileMenu.browseEventsByCity" />
      </NamedLink>
      <NamedLink className={css.mobileDirectLink} name="SeoPerformersIndexPage">
        <FormattedMessage id="TopbarMobileMenu.browseEventsByPerformer" />
      </NamedLink>
    </div>
  );
};

export default TopbarBrowseCategoriesMegaMenu;
